import React, { useState, useEffect, useRef } from 'react';
import { X, MessageCircle, Send, Phone, Mail, FileText, CreditCard, Shield, Users } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { companyInfo } from '../mock';

const ChatbotWidget = () => {
  const { language } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      // Initial greeting
      const greeting = {
        type: 'bot',
        content: language === 'en' 
          ? "Hi! I'm Segurito, Palerm Insurance Agency's virtual assistant. I'm here to help with basic questions. Please note I'm a virtual assistant, not a human agent. How can I assist you today?"
          : "¡Hola! Soy Segurito, el asistente virtual de Palerm Insurance Agency. Estoy aquí para ayudar con preguntas básicas. Por favor note que soy un asistente virtual, no un agente humano. ¿Cómo puedo ayudarte hoy?",
        timestamp: new Date()
      };
      setMessages([greeting]);
    }
  }, [isOpen, language]);

  const quickActions = {
    en: [
      { id: 'quote', label: 'Get a Quote', icon: FileText },
      { id: 'coi', label: 'Request COI', icon: Shield },
      { id: 'id', label: 'ID Cards', icon: CreditCard },
      { id: 'claims', label: 'File Claim', icon: FileText },
      { id: 'contact', label: 'Talk to Team', icon: Users }
    ],
    es: [
      { id: 'quote', label: 'Cotizar', icon: FileText },
      { id: 'coi', label: 'Solicitar COI', icon: Shield },
      { id: 'id', label: 'Tarjetas ID', icon: CreditCard },
      { id: 'claims', label: 'Reclamación', icon: FileText },
      { id: 'contact', label: 'Hablar con Equipo', icon: Users }
    ]
  };

  const responses = {
    en: {
      quote: "To get a personalized insurance quote:\n\n📞 Call: 469-434-8287\n📧 Email: info@palerminsurance.com\n📠 Fax: 800-887-4484\n\nYou can also visit our product pages to learn more about available coverage options.",
      coi: "To request a Certificate of Insurance (COI):\n\n1. You'll need:\n   • Insured name\n   • Policy number\n   • Certificate holder information\n   • Any special requirements\n\n2. Contact us:\n   📞 469-434-8287\n   📧 info@palerminsurance.com\n   📠 Fax: 800-887-4484\n\n⚠️ Note: I cannot issue or confirm coverage. Our team will process your request.",
      id: "To request insurance ID cards:\n\n📞 Call: 469-434-8287\n📧 Email: info@palerminsurance.com\n\nOur team will assist you with digital or physical ID cards for your policy.",
      claims: "To file an insurance claim:\n\n📞 Call: 469-434-8287\n📧 Email: info@palerminsurance.com\n\nOur team will guide you through the claims process and required documentation.",
      contact: "Contact Palerm Insurance Agency:\n\n📞 Phone: 469-434-8287\n📠 Fax: 800-887-4484\n📧 Email: info@palerminsurance.com\n📍 Address: 400 Stonebrook Pkwy Unit 503, Frisco, TX 75036\n\nVisit our 'Our Agents' page to contact specific team members directly.",
      hours: "For office hours and availability, please contact our team:\n📞 469-434-8287\n📧 info@palerminsurance.com",
      default: "I'm sorry, I don't have information about that. For detailed assistance, please contact our team:\n\n📞 469-434-8287\n📧 info@palerminsurance.com\n\nYou can also visit our FAQ page for more information."
    },
    es: {
      quote: "Para obtener una cotización personalizada de seguro:\n\n📞 Llamar: 469-434-8287\n📧 Email: info@palerminsurance.com\n📠 Fax: 800-887-4484\n\nTambién puedes visitar nuestras páginas de productos para conocer más sobre las opciones de cobertura disponibles.",
      coi: "Para solicitar un Certificado de Seguro (COI):\n\n1. Necesitarás:\n   • Nombre del asegurado\n   • Número de póliza\n   • Información del certificate holder\n   • Requisitos especiales\n\n2. Contáctanos:\n   📞 469-434-8287\n   📧 info@palerminsurance.com\n   📠 Fax: 800-887-4484\n\n⚠️ Nota: No puedo emitir ni confirmar coberturas. Nuestro equipo procesará tu solicitud.",
      id: "Para solicitar tarjetas de identificación de seguro:\n\n📞 Llamar: 469-434-8287\n📧 Email: info@palerminsurance.com\n\nNuestro equipo te asistirá con tarjetas ID digitales o físicas para tu póliza.",
      claims: "Para presentar una reclamación de seguro:\n\n📞 Llamar: 469-434-8287\n📧 Email: info@palerminsurance.com\n\nNuestro equipo te guiará a través del proceso de reclamaciones y la documentación requerida.",
      contact: "Contactar a Palerm Insurance Agency:\n\n📞 Teléfono: 469-434-8287\n📠 Fax: 800-887-4484\n📧 Email: info@palerminsurance.com\n📍 Dirección: 400 Stonebrook Pkwy Unit 503, Frisco, TX 75036\n\nVisita nuestra página 'Nuestros Agentes' para contactar a miembros específicos del equipo directamente.",
      hours: "Para horarios de oficina y disponibilidad, por favor contacta a nuestro equipo:\n📞 469-434-8287\n📧 info@palerminsurance.com",
      default: "Lo siento, no tengo información sobre eso. Para asistencia detallada, por favor contacta a nuestro equipo:\n\n📞 469-434-8287\n📧 info@palerminsurance.com\n\nTambién puedes visitar nuestra página de Preguntas Frecuentes para más información."
    }
  };

  const handleQuickAction = (actionId) => {
    const userMessage = {
      type: 'user',
      content: quickActions[language].find(a => a.id === actionId).label,
      timestamp: new Date()
    };

    const botResponse = {
      type: 'bot',
      content: responses[language][actionId] || responses[language].default,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage, botResponse]);
  };

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (!inputValue.trim()) return;

    const userMessage = {
      type: 'user',
      content: inputValue,
      timestamp: new Date()
    };

    // Simple keyword matching for common questions
    const lowerInput = inputValue.toLowerCase();
    let responseKey = 'default';

    if (lowerInput.includes('quote') || lowerInput.includes('cotiz')) {
      responseKey = 'quote';
    } else if (lowerInput.includes('coi') || lowerInput.includes('certificate') || lowerInput.includes('certificado')) {
      responseKey = 'coi';
    } else if (lowerInput.includes('id card') || lowerInput.includes('tarjeta')) {
      responseKey = 'id';
    } else if (lowerInput.includes('claim') || lowerInput.includes('reclamaci')) {
      responseKey = 'claims';
    } else if (lowerInput.includes('contact') || lowerInput.includes('phone') || lowerInput.includes('email') || lowerInput.includes('contacto') || lowerInput.includes('teléfono')) {
      responseKey = 'contact';
    } else if (lowerInput.includes('hour') || lowerInput.includes('horario') || lowerInput.includes('open')) {
      responseKey = 'hours';
    }

    const botResponse = {
      type: 'bot',
      content: responses[language][responseKey],
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage, botResponse]);
    setInputValue('');
  };

  const toggleChat = () => {
    setIsOpen(!isOpen);
  };

  if (!isOpen) {
    return (
      <button
        onClick={toggleChat}
        className="fixed bottom-6 right-6 z-50 bg-blue-600 hover:bg-blue-700 text-white rounded-full p-4 shadow-2xl transition-all duration-300 hover:scale-110 focus:outline-none focus:ring-4 focus:ring-blue-300"
        aria-label={language === 'en' ? 'Open chat with Segurito' : 'Abrir chat con Segurito'}
      >
        <MessageCircle className="w-6 h-6" />
      </button>
    );
  }

  return (
    <div className="fixed bottom-6 right-6 z-50 w-full max-w-md">
      <div className="bg-white rounded-lg shadow-2xl flex flex-col h-[600px] max-h-[80vh]">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4 rounded-t-lg flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center overflow-hidden">
              <img 
                src="/images/segurito-avatar.png" 
                alt="Segurito" 
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.parentElement.innerHTML = '<span class="text-2xl">🐘</span>';
                }}
              />
            </div>
            <div>
              <h3 className="font-bold text-lg">Segurito</h3>
              <p className="text-xs text-blue-100">
                {language === 'en' ? 'Virtual Assistant' : 'Asistente Virtual'}
              </p>
              <p className="text-xs text-blue-200 italic">
                {language === 'en' ? 'Not a human agent' : 'No es un agente humano'}
              </p>
            </div>
          </div>
          <button
            onClick={toggleChat}
            className="hover:bg-blue-800 rounded-full p-2 transition-colors focus:outline-none focus:ring-2 focus:ring-white"
            aria-label={language === 'en' ? 'Close chat' : 'Cerrar chat'}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
          {messages.map((message, index) => (
            <div
              key={index}
              className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[80%] rounded-lg p-3 ${
                  message.type === 'user'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-800 shadow-md'
                }`}
              >
                <p className="text-sm whitespace-pre-line">{message.content}</p>
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>

        {/* Quick Actions */}
        <div className="border-t border-gray-200 p-3 bg-white">
          <p className="text-xs text-gray-600 mb-2 font-semibold">
            {language === 'en' ? 'Quick Actions:' : 'Acciones Rápidas:'}
          </p>
          <div className="flex flex-wrap gap-2">
            {quickActions[language].map((action) => {
              const Icon = action.icon;
              return (
                <button
                  key={action.id}
                  onClick={() => handleQuickAction(action.id)}
                  className="flex items-center gap-1 px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-full text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-300"
                >
                  <Icon className="w-3 h-3" />
                  {action.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Input */}
        <form onSubmit={handleSendMessage} className="border-t border-gray-200 p-4 bg-white rounded-b-lg">
          <div className="flex gap-2">
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder={language === 'en' ? 'Type your question...' : 'Escribe tu pregunta...'}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            />
            <button
              type="submit"
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-full p-2 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-300"
              aria-label={language === 'en' ? 'Send message' : 'Enviar mensaje'}
            >
              <Send className="w-5 h-5" />
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-2 text-center">
            {language === 'en' 
              ? '⚠️ Do not share sensitive information in this chat' 
              : '⚠️ No compartas información sensible en este chat'}
          </p>
        </form>
      </div>
    </div>
  );
};

export default ChatbotWidget;
